import {Header} from '@/components/commons/header/Header'
import {SettingsSidebar} from '@/components/commons/settings-sidebar/SettingsSidebar'
import {Button} from '@/components/ui/button/Button'
import {PlusIcon, AlertTriangleIcon, Pencil01Icon, Trash01Icon} from '@/components/ui/icon/Icon'
import {PageHeading} from '@/components/ui/page-atoms/PageAtoms'
import {Text} from '@/components/ui/text/Text'
import {TKey, useTranslation} from '@/translations/i18n'
import {Helmet} from 'react-helmet'
import {StyledCardWrapper, StyledPageBody, StyledPageContent, StyledPageWrapper} from './style'
import {FiltersBar} from '@/components/ui/filters-bar/FiltersBar'
import {useGetReferringSpecialists} from '@/features/referring-specialists/queries/useGetReferringSpecialists'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {Table} from '@/components/commons/table/Table'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {TableBodyCell, TableBodyCellText} from '@/components/ui/table-atoms/TableAtoms'
import {ChangeEvent, useRef} from 'react'
import {debounce} from '@/utilities/helpers'
import {TableInputSearch} from '@/components/commons/table-input-search/TableInputSearch'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {z} from 'zod'
import {ReferringSpecialistsColumn} from '@/features/referring-specialists/types'
import {formatLocaleDate} from '@/dayjs'
import {MoreActionsCell} from '@/components/ui/more-actions-cell/MoreActionsCell'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {TableBodyRow} from '@/components/ui/table-body-row/TableBodyRow'
import {DeleteReferringSpecialist} from '@/features/referring-specialists/components/delete-referring-specialist/DeleteReferringSpecialist'
import {CreateUpdateReferringSpecialist} from '@/features/referring-specialists/components/create-update-referring-specialist/CreateUpdateReferringSpecialist'
import {usePermissions} from '@/features/auth/store/store'
import {NoPermissionBox} from '@/components/commons/no-permission-box/NoPermissionBox'

const columnNameToLabel = {
    [ReferringSpecialistsColumn.enum.fullName]: 'commons:name_header',
    [ReferringSpecialistsColumn.enum.serviceUoc]: 'referring_specialist:uoc_service',
    [ReferringSpecialistsColumn.enum.email]: 'commons:email',
    [ReferringSpecialistsColumn.enum.createdAt]: 'referring_specialist:added_date'
} as const satisfies Record<Exclude<ReferringSpecialistsColumn, 'select' | 'moreActions'>, TKey | null>

export const ReferringSpecialists = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const {searchParams, setSearchParams} = useParsedSearchParams(
        z.object({
            limit: z.coerce.number().int().positive().default(50),
            page: z.coerce.number().int().positive().default(1),
            search: z.string().trim().optional()
        })
    )
    const inputSearchRef = useRef<HTMLInputElement | null>(null)

    const referringSpecialistsQuery = useGetReferringSpecialists({
        limit: searchParams.limit,
        page: searchParams.page,
        search: searchParams.search
    })

    const onSearch = debounce((event: ChangeEvent<HTMLInputElement>) => {
        setSearchParams({search: event.target.value || undefined, page: 1})
    }, 1000)

    if (!permissions?.settings?.read) {
        return <NoPermissionBox />
    }

    return (
        <StyledPageWrapper>
            <Helmet title={t('referring_specialist:meta_title')} />
            <SettingsSidebar />
            <StyledPageBody>
                <Header />
                <StyledPageContent>
                    <StyledCardWrapper>
                        <PageHeading justify="space-between">
                            <Text render="h1" size="textXl" weight="500">
                                {t('referring_specialist:title')}
                            </Text>
                            <CreateUpdateReferringSpecialist
                                trigger={
                                    <Button variant="primaryNeutral">
                                        <PlusIcon />
                                        {t('referring_specialist:add_specialist')}
                                    </Button>
                                }
                            />
                        </PageHeading>
                        <FiltersBar>
                            <Flexbox align="center" gap={2}>
                                <TableInputSearch
                                    ref={inputSearchRef}
                                    defaultValue={searchParams.search}
                                    onChange={onSearch}
                                    placeholder={t('commons:search_name')}
                                />
                            </Flexbox>
                        </FiltersBar>
                        {referringSpecialistsQuery.isError ? (
                            <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
                        ) : (
                            <Table
                                emptyStateComponent={
                                    <EmptyState
                                        title={t('referring_specialist:no_specialists_found')}
                                        subtitle={t('referring_specialist:no_specialists_found_description')}
                                    >
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => {
                                                setSearchParams({search: undefined, page: 1})
                                                if (inputSearchRef.current) {
                                                    inputSearchRef.current.value = ''
                                                }
                                            }}
                                        >
                                            {t('referring_specialist:no_specialists_found_cta')}
                                        </Button>
                                    </EmptyState>
                                }
                                pagination={{
                                    ...referringSpecialistsQuery.persistedPagination,
                                    currentPage: searchParams.page,
                                    limit: searchParams.limit,
                                    onChangePage: page => setSearchParams({page}),
                                    onChangeLimit: limit => {
                                        setSearchParams({limit, page: 1})
                                    }
                                }}
                                columnPinning={{
                                    right: [ReferringSpecialistsColumn.enum.moreActions]
                                }}
                                rowComponent={({tableBodyRowProps, index}) => (
                                    <TableBodyRow
                                        key={index}
                                        {...tableBodyRowProps}
                                        style={{...tableBodyRowProps.style, cursor: 'initial'}}
                                    />
                                )}
                                isLoading={referringSpecialistsQuery.isPending}
                                data={referringSpecialistsQuery.data?.data ?? []}
                                columns={[
                                    {
                                        cell: ({dataItem, forwardedProps}) => (
                                            <TableBodyCell {...forwardedProps}>
                                                <TableBodyCellText variant="dark">
                                                    {dataItem.firstName} {dataItem.lastName}
                                                </TableBodyCellText>
                                            </TableBodyCell>
                                        ),
                                        header: () => t(columnNameToLabel[ReferringSpecialistsColumn.enum.fullName]),
                                        id: ReferringSpecialistsColumn.enum.fullName,
                                        minSize: () => 80,
                                        size: () => 300,
                                        maxSize: () => 400
                                    },
                                    {
                                        cell: ({dataItem, forwardedProps}) => (
                                            <TableBodyCell {...forwardedProps}>
                                                <TableBodyCellText variant="dark">
                                                    {dataItem.serviceUoc}
                                                </TableBodyCellText>
                                            </TableBodyCell>
                                        ),
                                        header: () => t(columnNameToLabel[ReferringSpecialistsColumn.enum.serviceUoc]),
                                        id: ReferringSpecialistsColumn.enum.serviceUoc,
                                        minSize: () => 80,
                                        size: () => 250,
                                        maxSize: () => 400
                                    },
                                    {
                                        cell: ({dataItem, forwardedProps}) => (
                                            <TableBodyCell {...forwardedProps}>
                                                <TableBodyCellText variant="dark">{dataItem.email}</TableBodyCellText>
                                            </TableBodyCell>
                                        ),
                                        header: () => t(columnNameToLabel[ReferringSpecialistsColumn.enum.email]),
                                        id: ReferringSpecialistsColumn.enum.email,
                                        minSize: () => 80,
                                        size: () => 250,
                                        maxSize: () => 400
                                    },
                                    {
                                        cell: ({dataItem, forwardedProps}) => (
                                            <TableBodyCell {...forwardedProps}>
                                                <TableBodyCellText variant="dark">
                                                    {formatLocaleDate(dataItem.createdAt, 'll')}
                                                </TableBodyCellText>
                                            </TableBodyCell>
                                        ),
                                        header: () => t(columnNameToLabel[ReferringSpecialistsColumn.enum.createdAt]),
                                        id: ReferringSpecialistsColumn.enum.createdAt,
                                        minSize: () => 80,
                                        size: () => 150,
                                        maxSize: () => 400
                                    },
                                    {
                                        cell: ({forwardedProps, dataItem}) => (
                                            <MoreActionsCell {...forwardedProps}>
                                                <Flexbox gap={3}>
                                                    <CreateUpdateReferringSpecialist
                                                        trigger={
                                                            <Button
                                                                size="xs"
                                                                shape="square"
                                                                variant="secondary"
                                                                className="show-on-hover"
                                                            >
                                                                <Pencil01Icon />
                                                            </Button>
                                                        }
                                                        referringSpecialist={dataItem}
                                                    />
                                                    {dataItem.canBeDeleted ? (
                                                        <DeleteReferringSpecialist id={dataItem.id} />
                                                    ) : (
                                                        <Tooltip
                                                            variant="light"
                                                            content={
                                                                <Flexbox direction="column">
                                                                    <Text render="h3" size="textXs" weight="500">
                                                                        {t('referring_specialist:cannot_delete_title')}
                                                                    </Text>
                                                                    <Text
                                                                        render="p"
                                                                        size="textXs"
                                                                        palette="neutral"
                                                                        shade="600"
                                                                    >
                                                                        {t(
                                                                            'referring_specialist:cannot_delete_description'
                                                                        )}
                                                                    </Text>
                                                                </Flexbox>
                                                            }
                                                            triggerProps={{asChild: true}}
                                                        >
                                                            <Button
                                                                className="show-on-hover"
                                                                disabled
                                                                size="xs"
                                                                shape="square"
                                                                variant="secondary"
                                                            >
                                                                <Trash01Icon />
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                </Flexbox>
                                            </MoreActionsCell>
                                        ),
                                        header: () => undefined,
                                        id: ReferringSpecialistsColumn.enum.moreActions,
                                        size: () => 124
                                    }
                                ]}
                            />
                        )}
                    </StyledCardWrapper>
                </StyledPageContent>
            </StyledPageBody>
        </StyledPageWrapper>
    )
}
